import React, { useCallback, useState } from "react";
import { useAppSelector, useAppDispatch } from "./store/hooks";
import {
    selectActiveEdgeDevices,
    startEditingNewEdgeDevice,
    startEditingNewWeatherSensor,
    startEditingExistingDevice,
    startEditingExistingWeatherSensor,
    submitEdgeDeviceDelete,
    submitWeatherSensorDelete,
    loadLatestDeviceDataDataset,
    selectDeviceDataDatasetStatus,
    selectDeviceUpdateStatus,
    selectExistingDeviceIdBeingEdited,
    selectCurrentDeviceBeingEdited,
    selectDeviceUpdateError,
    selectActiveWeatherSensors,
} from "./store/reducers/deviceLinkingAppStoreSlice";
import {
    ListItem,
    List,
    ListItemButton,
    Button,
    ListItemSecondaryAction,
    Dialog,
    DialogActions,
    DialogTitle,
    DialogContent,
    DialogContentText,
    CircularProgress,
    Checkbox,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import makeStyles from "@mui/styles/makeStyles";
import type { Device, WeatherSensor } from "../types";
import ErrorText from "./ErrorText";
import ReplaceWeatherSensorForm from "./ReplaceWeatherSensorForm";

const useStyles = makeStyles((theme) => ({
    deviceButtonsContainer: {
        display: "flex",
    },
}));

type DevicesAtLocationListProps = {
    className?: string;
};

export default function DevicesAtLocationList(props: DevicesAtLocationListProps): JSX.Element {
    const classes = useStyles();
    const dispatch = useAppDispatch();
    const selectedEdgeDevices = useAppSelector(selectActiveEdgeDevices);
    const selectedWeatherSensors = useAppSelector(selectActiveWeatherSensors);

    const [deviceTypeToDelete, setDeviceTypeToDelete] = useState<"edge-device" | "weather-sensor" | undefined>();
    const [edgeDeviceToDelete, setEdgeDeviceToDelete] = useState<Device | undefined>();
    const [weatherSensorToDelete, setWeatherSensorToDelete] = useState<WeatherSensor | undefined>();
    const [showReplaceWeatherSensorDialog, setShowReplaceWeatherSensorDialog] = useState(false);
    const [weatherSensorToReplace, setWeatherSensorToReplace] = useState<WeatherSensor | undefined>();

    const deviceDatasetStatus = useAppSelector(selectDeviceDataDatasetStatus);
    const deviceUpdateStatus = useAppSelector(selectDeviceUpdateStatus);
    const deviceUpdateError = useAppSelector(selectDeviceUpdateError);

    const currentDeviceBeingEdited = useAppSelector(selectCurrentDeviceBeingEdited);
    const existingDeviceIdBeingEdited = useAppSelector(selectExistingDeviceIdBeingEdited);

    const [skipMasterdataSync, setSkipMasterdataSync] = useState(false);

    const onEditEdgeDevice = useCallback((device: Device) => {
        dispatch(startEditingExistingDevice(device.edge_device_id));
    }, []);
    const onEditWeatherSensor = useCallback((weather_sensor: WeatherSensor) => {
        dispatch(startEditingExistingWeatherSensor(weather_sensor.device_sn));
    }, []);

    const onReplaceWeatherSensor = useCallback((weather_sensor: WeatherSensor) => {
        setWeatherSensorToReplace(weather_sensor);
        setShowReplaceWeatherSensorDialog(true);
        // Reload dataset to ensure we have the latest version for optimistic locking
        dispatch(loadLatestDeviceDataDataset());
    }, [dispatch]);

    const onCloseReplaceWeatherSensorDialog = useCallback(() => {
        setShowReplaceWeatherSensorDialog(false);
        setWeatherSensorToReplace(undefined);
        // Reload dataset after replacement to show the new state
        dispatch(loadLatestDeviceDataDataset());
    }, [dispatch]);

    const onSelectEdgeDeviceToDelete = useCallback((edgeDevice: Device) => {
        setDeviceTypeToDelete("edge-device");
        setEdgeDeviceToDelete(edgeDevice);
        setWeatherSensorToDelete(undefined);
    }, []);
    const onSelectWeatherSensorToDelete = useCallback((weatherSensor: WeatherSensor) => {
        setDeviceTypeToDelete("weather-sensor");
        setWeatherSensorToDelete(weatherSensor);
        setEdgeDeviceToDelete(undefined);
    }, []);

    const onCancelDeviceDelete = useCallback(() => {
        if (deviceTypeToDelete === "edge-device") {
            setEdgeDeviceToDelete(undefined);
        } else {
            setWeatherSensorToDelete(undefined);
        }
        setDeviceTypeToDelete(undefined);
    }, [deviceTypeToDelete]);

    const onDeleteDevice = useCallback(async () => {
        if (deviceTypeToDelete === "edge-device") {
            const edgeDevice = edgeDeviceToDelete as Device;
            const result = await dispatch(submitEdgeDeviceDelete({ edgeDeviceId: edgeDevice.edge_device_id, skipMasterdataSync }));

            if (submitEdgeDeviceDelete.fulfilled.match(result)) {
                setEdgeDeviceToDelete(undefined);
                setDeviceTypeToDelete(undefined);
                setSkipMasterdataSync(false);
                dispatch(loadLatestDeviceDataDataset());
                alert(`Edge device ${edgeDevice.edge_device_id} has been deleted successfully!`);
            }
            // On failure, keep the dialog open so the error remains visible
        } else {
            const weatherSensor = weatherSensorToDelete as WeatherSensor;
            const result = await dispatch(submitWeatherSensorDelete({ weatherSensorSn: weatherSensor.device_sn, skipMasterdataSync }));

            if (submitWeatherSensorDelete.fulfilled.match(result)) {
                setWeatherSensorToDelete(undefined);
                setDeviceTypeToDelete(undefined);
                setSkipMasterdataSync(false);
                dispatch(loadLatestDeviceDataDataset());
                alert(`Weather sensor ${weatherSensor.device_sn} has been deleted successfully!`);
            }
            // On failure, keep the dialog open so the error remains visible
        }
    }, [
        deviceTypeToDelete,
        edgeDeviceToDelete,
        weatherSensorToDelete,
        skipMasterdataSync,
        // Below dependencies are setters, we should not expect them to change
        dispatch,
        setEdgeDeviceToDelete,
        submitEdgeDeviceDelete,
        setWeatherSensorToDelete,
        submitWeatherSensorDelete,
        setDeviceTypeToDelete,
        loadLatestDeviceDataDataset,
        setSkipMasterdataSync,
    ]);

    return (
        <div className={props.className}>
            <h1>Devices at location</h1>
            {deviceDatasetStatus === "loading" || deviceUpdateStatus === "loading" ? (
                <CircularProgress />
            ) : (
                <>
                    {(selectedEdgeDevices && selectedEdgeDevices.length >= 1) ||
                    (selectedWeatherSensors && selectedWeatherSensors.length >= 1) ? (
                        <>
                            {selectedEdgeDevices && selectedEdgeDevices.length >= 1 && (
                                <>
                                    <h2>Edge Devices</h2>
                                    <List>
                                        {selectedEdgeDevices.map((edge_device) => {
                                            return (
                                                <ListItem key={edge_device.edge_device_id}>
                                                    {edge_device.edge_device_id} / {edge_device.cloudgate_serial_no} /
                                                    Site {edge_device.site_id}
                                                    <ListItemSecondaryAction>
                                                        <div className={classes.deviceButtonsContainer}>
                                                            <ListItemButton>
                                                                <EditIcon
                                                                    onClick={() => onEditEdgeDevice(edge_device)}
                                                                />
                                                            </ListItemButton>
                                                            <ListItemButton
                                                                disabled={
                                                                    existingDeviceIdBeingEdited ===
                                                                    edge_device.edge_device_id
                                                                }
                                                            >
                                                                <DeleteIcon
                                                                    onClick={() =>
                                                                        onSelectEdgeDeviceToDelete(edge_device)
                                                                    }
                                                                />
                                                            </ListItemButton>
                                                        </div>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                </>
                            )}
                            {selectedWeatherSensors && selectedWeatherSensors.length >= 1 && (
                                <>
                                    <h2>Weather Sensors</h2>
                                    <List>
                                        {selectedWeatherSensors.map((weather_sensor) => {
                                            return (
                                                <ListItem key={weather_sensor.device_sn}>
                                                    {weather_sensor.device_sn} / Site {weather_sensor.site_id}
                                                    <ListItemSecondaryAction>
                                                        <div className={classes.deviceButtonsContainer}>
                                                            <ListItemButton>
                                                                <EditIcon
                                                                    onClick={() => onEditWeatherSensor(weather_sensor)}
                                                                />
                                                            </ListItemButton>
                                                            <ListItemButton
                                                                disabled={
                                                                    existingDeviceIdBeingEdited ===
                                                                    weather_sensor.device_sn
                                                                }
                                                            >
                                                                <SwapHorizIcon
                                                                    onClick={() => onReplaceWeatherSensor(weather_sensor)}
                                                                    titleAccess="Replace sensor"
                                                                />
                                                            </ListItemButton>
                                                            <ListItemButton
                                                                disabled={
                                                                    existingDeviceIdBeingEdited ===
                                                                    weather_sensor.device_sn
                                                                }
                                                            >
                                                                <DeleteIcon
                                                                    onClick={() =>
                                                                        onSelectWeatherSensorToDelete(weather_sensor)
                                                                    }
                                                                />
                                                            </ListItemButton>
                                                        </div>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                </>
                            )}
                        </>
                    ) : (
                        <p>No devices...</p>
                    )}
                    <Button onClick={() => dispatch(startEditingNewEdgeDevice())}>Add Edge Device</Button>
                    <Button onClick={() => dispatch(startEditingNewWeatherSensor())}>Add Weather Sensor</Button>
                    {!currentDeviceBeingEdited && deviceUpdateStatus === "failed" && deviceUpdateError && (
                        <ErrorText error={deviceUpdateError} />
                    )}
                    <Dialog
                        open={deviceTypeToDelete !== undefined}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">{"Confirm Device Deletion"}</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                Are you sure you wish to delete this device:{" "}
                                {deviceTypeToDelete === "edge-device" ? (
                                    <b>
                                        {edgeDeviceToDelete?.edge_device_id} / {edgeDeviceToDelete?.cloudgate_serial_no}{" "}
                                        / Site {edgeDeviceToDelete?.site_id}
                                    </b>
                                ) : (
                                    <b>
                                        {weatherSensorToDelete?.device_sn} / Site {weatherSensorToDelete?.site_id}
                                    </b>
                                )}
                                ? The device can be recovered using an old version of the <i>device-data</i> dataset.
                            </DialogContentText>
                            <DialogContentText>
                                <Checkbox
                                    checked={skipMasterdataSync}
                                    onChange={(event) => setSkipMasterdataSync(event.target.checked)}
                                />
                                Skip sync to masterdata (FOR INTERNAL USE ONLY)
                            </DialogContentText>
                            {deviceUpdateStatus === "failed" && deviceUpdateError && (
                                <ErrorText error={deviceUpdateError} />
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => onCancelDeviceDelete()}>Cancel</Button>
                            <Button onClick={() => onDeleteDevice()} autoFocus>
                                Delete
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {showReplaceWeatherSensorDialog && (
                        <Dialog
                            open={showReplaceWeatherSensorDialog}
                            onClose={onCloseReplaceWeatherSensorDialog}
                            maxWidth="md"
                            fullWidth
                        >
                            <DialogTitle>Replace Weather Sensor</DialogTitle>
                            <DialogContent>
                                <ReplaceWeatherSensorForm
                                    onClose={onCloseReplaceWeatherSensorDialog}
                                    initialSensorSn={weatherSensorToReplace?.device_sn}
                                />
                            </DialogContent>
                        </Dialog>
                    )}
                </>
            )}
        </div>
    );
}
